// frontend/src/components/ArtworkPage/ArtworkPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../../services/api";
import ArtworkDisplay from "./ArtworkDisplay";
import ArtworkDetails from "./ArtworkDetails";
import NavigationButtons from "./NavigationButtons";
import LoadingSpinner from "../common/LoadingSpinner";
import useKeyboardNavigation from "../../hooks/useKeyboardNavigation";
import "./ArtworkPage.css";

const ArtworkPage = () => {
  const { artworkId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentArtwork, setCurrentArtwork] = useState(null);
  const [artworkList, setArtworkList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Pagination state - initialize with defaults to prevent undefined
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [pageLimit, setPageLimit] = useState(12);

  // Context state
  const [categoryId, setCategoryId] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [tagId, setTagId] = useState(null);
  const [tagName, setTagName] = useState("");
  const [artistId, setArtistId] = useState(null);
  const [artistName, setArtistName] = useState("");

  // Load artwork and list from location state or API
  useEffect(() => {
    const loadArtwork = async () => {
      try {
        setLoading(true);

        if (location.state?.artworkList) {
          const list = location.state.artworkList;
          const index = list.findIndex((a) => a._id === artworkId);

          setArtworkList(list);
          setCurrentIndex(index >= 0 ? index : 0);
          setCurrentArtwork(list[index >= 0 ? index : 0]);

          // Set pagination state from location - with fallbacks
          setHasMore(location.state.hasMore ?? false);
          setCurrentPage(location.state.page ?? 1);
          setPageLimit(location.state.limit ?? 12);

          // Check if coming from category/tag context
          if (location.state.categoryId) {
            setCategoryId(location.state.categoryId);
            setCategoryName(location.state.categoryName || "");
            setTagId(location.state.tagId || null);
            setTagName(location.state.tagName || "");
            setArtistId(null);
            setArtistName("");
          }
          // Check if coming from artist context
          else if (location.state.artistId) {
            setArtistId(location.state.artistId);
            setArtistName(location.state.artistName || "");
            setCategoryId(null);
            setCategoryName("");
            setTagId(null);
            setTagName("");
          }
        } else {
          // Fallback: load single artwork if no state provided
          const artwork = await api.getArtwork(artworkId);
          setCurrentArtwork(artwork);

          if (artwork.categoryId) {
            const categoryIdValue =
              typeof artwork.categoryId === "object"
                ? artwork.categoryId._id
                : artwork.categoryId;
            setCategoryId(categoryIdValue);
            setCategoryName(
              typeof artwork.categoryId === "object"
                ? artwork.categoryId.name || ""
                : "",
            );

            const data = await api.getArtworks(categoryIdValue, 1, 100);
            setArtworkList(data.artworks || []);
            const foundIndex = (data.artworks || []).findIndex(
              (a) => a._id === artworkId,
            );
            setCurrentIndex(foundIndex >= 0 ? foundIndex : 0);
            setHasMore(data.hasMore ?? false);
            setCurrentPage(1);
            setPageLimit(data.limit || 12);
          } else {
            setArtworkList([artwork]);
            setCurrentIndex(0);
            setHasMore(false);
          }
        }
      } catch (err) {
        console.error("Error loading artwork:", err);
      } finally {
        setLoading(false);
      }
    };

    loadArtwork();
  }, [artworkId, location.state]);

  // Load next page of artworks when needed
  const loadNextPage = async () => {
    if (!hasMore || loadingMore) return null;

    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;

      let data;

      // Call appropriate API based on context
      if (categoryId && tagId) {
        data = await api.getArtworksByTag(
          categoryId,
          tagId,
          nextPage,
          pageLimit,
        );
      } else if (artistId) {
        data = await api.getArtworksByArtist(artistId, nextPage, pageLimit);
      } else {
        return null;
      }

      if (!data || !data.artworks) {
        return null;
      }

      // Append new artworks to the list
      const updatedList = [...artworkList, ...data.artworks];
      setArtworkList(updatedList);
      setHasMore(data.hasMore ?? false);
      setCurrentPage(nextPage);

      return data.artworks;
    } catch (err) {
      console.error("Error loading next page:", err);
      return null;
    } finally {
      setLoadingMore(false);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      const newArtwork = artworkList[newIndex];
      navigate(`/artwork/${newArtwork._id}`, {
        state: {
          artworkList,
          categoryId,
          categoryName,
          tagId,
          tagName,
          artistId,
          artistName,
          hasMore,
          page: currentPage,
          limit: pageLimit,
        },
        replace: true,
      });
    }
  };

  const goToNext = async () => {
    const isLastInList = currentIndex >= artworkList.length - 1;

    // If at the last item and there are more pages, load next page
    if (isLastInList && hasMore) {
      const newArtworks = await loadNextPage();

      if (newArtworks && newArtworks.length > 0) {
        // Navigate to first artwork of newly loaded page
        const newIndex = currentIndex + 1;
        const updatedList = [...artworkList, ...newArtworks];
        const newArtwork = updatedList[newIndex];

        navigate(`/artwork/${newArtwork._id}`, {
          state: {
            artworkList: updatedList,
            categoryId,
            categoryName,
            tagId,
            tagName,
            artistId,
            artistName,
            hasMore, // Will be updated by loadNextPage
            page: currentPage + 1,
            limit: pageLimit,
          },
          replace: true,
        });
      }
    } else if (currentIndex < artworkList.length - 1) {
      // Normal navigation within loaded artworks
      const newIndex = currentIndex + 1;
      const newArtwork = artworkList[newIndex];
      navigate(`/artwork/${newArtwork._id}`, {
        state: {
          artworkList,
          categoryId,
          categoryName,
          tagId,
          tagName,
          artistId,
          artistName,
          hasMore,
          page: currentPage,
          limit: pageLimit,
        },
        replace: true,
      });
    }
  };

  const handleBackToTag = () => {
    if (categoryId && tagId) {
      navigate(`/category/${categoryId}/tag/${tagId}`);
    }
  };

  const handleBackToArtist = () => {
    if (artistId) {
      navigate(`/artist/${artistId}`);
    }
  };

  // Calculate navigation availability
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < artworkList.length - 1 || hasMore;

  useKeyboardNavigation(
    goToPrevious,
    goToNext,
    currentIndex,
    artworkList.length,
    hasMore,
  );

  if (loading || !currentArtwork) {
    return <LoadingSpinner message="Loading artwork..." />;
  }

  return (
    <div>
      <NavigationButtons
        onPrevious={goToPrevious}
        onNext={goToNext}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        loading={loadingMore}
      />

      <div className="artwork-main">
        <div className="artwork-content">
          {/* Breadcrumb Navigation - Category/Tag Context */}
          {categoryId && tagId && (
            <nav className="artwork-breadcrumb">
              <button
                className="artwork-breadcrumb-link"
                onClick={handleBackToTag}
                aria-label={`Back to ${categoryName} ${tagName}`}
              >
                <svg
                  className="artwork-breadcrumb-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                {categoryName} - {tagName}
              </button>
              <span className="artwork-breadcrumb-separator">/</span>
              <span className="artwork-breadcrumb-current">
                {currentArtwork.title}
              </span>
            </nav>
          )}

          {/* Breadcrumb Navigation - Artist Context */}
          {artistId && !categoryId && !tagId && (
            <nav className="artwork-breadcrumb">
              <button
                className="artwork-breadcrumb-link"
                onClick={handleBackToArtist}
                aria-label={`Back to ${artistName} artworks`}
              >
                <svg
                  className="artwork-breadcrumb-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                {artistName}
              </button>
              <span className="artwork-breadcrumb-separator">/</span>
              <span className="artwork-breadcrumb-current">
                {currentArtwork.title}
              </span>
            </nav>
          )}

          <ArtworkDisplay artwork={currentArtwork} />
          <ArtworkDetails
            artwork={currentArtwork}
            currentIndex={currentIndex}
            totalCount={artworkList.length}
            hasMore={hasMore}
          />
        </div>
      </div>
    </div>
  );
};

export default ArtworkPage;
